/**
 * Copyright © 2026 Mike Hall
 * Licensed under the GNU Affero General Public License v3.0.
 */
import { DEFAULT_MATCH_PARAMS, isStartTurn } from '@phalanxduel/shared';
import { createDeck, shuffleDeck } from './deck.js';
function emptyBattlefield(rows, columns) {
    return Array(rows * columns).fill(null);
}
function createPlayerState(id, name, seed, startingLifepoints, rows, columns) {
    const deck = createDeck();
    const drawpile = shuffleDeck(deck, seed);
    return {
        player: { id, name },
        hand: [],
        battlefield: emptyBattlefield(rows, columns),
        drawpile,
        discardPile: [],
        lifepoints: startingLifepoints,
        deckSeed: seed,
        handCount: 0,
        drawpileCount: drawpile.length,
        discardPileCount: 0,
    };
}
/**
 * Pre-deploy cards from a player's hand onto their battlefield.
 * Fills columns left-to-right, front row first (same order as getDeployTarget).
 * Cards are placed face-up (revealed) since there is no deployment phase to reveal them.
 */
function quickDeployPlayer(player, rows, columns) {
    const totalSlots = rows * columns;
    const toDeploy = Math.min(player.hand.length, totalSlots);
    const newBattlefield = [...player.battlefield];
    const newHand = [...player.hand];
    let deployed = 0;
    for (let col = 0; col < columns && deployed < toDeploy; col++) {
        for (let row = 0; row < rows && deployed < toDeploy; row++) {
            const gridIndex = row * columns + col;
            if (newBattlefield[gridIndex] !== null)
                continue;
            const card = newHand.splice(0, 1)[0];
            if (!card)
                throw new Error('Expected card in hand during quick deploy');
            const bfCard = {
                card,
                position: { row, col },
                currentHp: card.value,
                faceDown: false,
            };
            newBattlefield[gridIndex] = bfCard;
            deployed++;
        }
    }
    return { ...player, hand: newHand, battlefield: newBattlefield };
}
/**
 * Create the initial game state for two players.
 * Each player gets a shuffled deck as their drawpile.
 * Uses different derived seeds for each player to avoid identical decks.
 */
export function createInitialState(config) {
    const { matchId, players, rngSeed } = config;
    const gameOptions = config.gameOptions;
    const resolvedMatchParams = config.matchParams
        ? { ...DEFAULT_MATCH_PARAMS, ...config.matchParams }
        : {
            ...DEFAULT_MATCH_PARAMS,
            modeClassicDeployment: gameOptions?.classicDeployment ?? DEFAULT_MATCH_PARAMS.modeClassicDeployment,
            modeQuickStart: gameOptions?.quickStart ?? DEFAULT_MATCH_PARAMS.modeQuickStart,
            modeDamagePersistence: gameOptions?.damageMode ?? DEFAULT_MATCH_PARAMS.modeDamagePersistence,
        };
    // Compatibility gameOptions is partial. Apply the GameOptionsSchema default
    // when a caller overrides an unrelated option but omits starting LP.
    const startingLifepoints = gameOptions?.startingLifepoints ?? 20;
    const modeClassicDeployment = resolvedMatchParams.modeClassicDeployment;
    const modeQuickStart = resolvedMatchParams.modeQuickStart;
    // Read grid dimensions from matchParams, falling back to DEFAULT_MATCH_PARAMS
    const rows = resolvedMatchParams.rows;
    const columns = resolvedMatchParams.columns;
    const initialDraw = resolvedMatchParams.initialDraw;
    const baseState = {
        matchId,
        specVersion: resolvedMatchParams.specVersion,
        params: resolvedMatchParams,
        players: [
            createPlayerState(players[0].id, players[0].name, rngSeed, startingLifepoints, rows, columns),
            createPlayerState(players[1].id, players[1].name, rngSeed ^ 0x12345678, startingLifepoints, rows, columns),
        ],
        activePlayerIndex: 0,
        phase: 'StartTurn',
        turnNumber: 0,
        passState: {
            consecutivePasses: [0, 0],
            totalPasses: [0, 0],
        },
    };
    const drawTimestamp = config.drawTimestamp ?? '1970-01-01T00:00:00.000Z';
    let state = baseState;
    state = drawCards(state, 0, initialDraw, drawTimestamp);
    state = drawCards(state, 1, initialDraw, drawTimestamp);
    // TASK-201: Check for victory conditions (like deck exhaustion) at match start.
    // We do this directly here to ensure the state reflects an immediate end
    // if the configuration is invalid (e.g. deck smaller than initial draw).
    const initialVictory = checkVictory(state);
    if (initialVictory) {
        state.phase = 'gameOver';
        state.outcome = { ...initialVictory, turnNumber: 0 };
    }
    // Quick start: pre-deploy cards from hand to battlefield, skipping DeploymentPhase
    if (modeQuickStart && modeClassicDeployment) {
        const player0 = state.players[0];
        const player1 = state.players[1];
        if (!player0 || !player1)
            throw new Error('Missing player state during quick start');
        const p0 = quickDeployPlayer(player0, rows, columns);
        const p1 = quickDeployPlayer(player1, rows, columns);
        state = { ...state, players: [p0, p1] };
    }
    return state;
}
export function getPlayer(state, playerIndex) {
    const player = state.players[playerIndex];
    if (!player) {
        throw new Error(`Invalid player index: ${playerIndex}`);
    }
    return player;
}
export function setPlayer(state, playerIndex, player) {
    const p0 = state.players[0];
    const p1 = state.players[1];
    if (!p0 || !p1)
        throw new Error('Missing player state in setPlayer');
    const syncedPlayer = {
        ...player,
        handCount: player.hand.length,
        drawpileCount: player.drawpile.length,
        discardPileCount: player.discardPile.length,
    };
    const players = [p0, p1];
    players[playerIndex] = syncedPlayer;
    return { ...state, players };
}
/**
 * Draw `count` cards from a player's drawpile into their hand.
 * Returns a new GameState with updated hand and drawpile.
 * Card IDs are generated upon drawing to ensure determinism.
 */
export function drawCards(state, playerIndex, count, timestamp) {
    const player = getPlayer(state, playerIndex);
    const actualCount = Math.min(count, player.drawpile.length);
    if (actualCount === 0)
        return state;
    const drawnPartial = player.drawpile.slice(0, actualCount);
    const remainingPile = player.drawpile.slice(actualCount);
    const drawn = drawnPartial.map((p, i) => {
        // Opaque ID: no card info encoded — prevents information leakage on face-down cards.
        // Deterministic for replay: same inputs always produce the same ID.
        const id = `${timestamp}::${state.matchId}::${player.player.id}::${state.turnNumber}::${i}`;
        return { ...p, id };
    });
    return setPlayer(state, playerIndex, {
        ...player,
        hand: [...player.hand, ...drawn],
        drawpile: remainingPile,
    });
}
/**
 * The position index maps to the 8-slot grid: 0-3 = front row (L-R), 4-7 = back row (L-R).
 */
export function deployCard(state, playerIndex, handCardIndex, gridIndex) {
    const player = getPlayer(state, playerIndex);
    if (handCardIndex < 0 || handCardIndex >= player.hand.length) {
        throw new Error(`Invalid hand card index: ${handCardIndex}`);
    }
    const totalSlots = state.params.rows * state.params.columns;
    if (gridIndex < 0 || gridIndex >= totalSlots) {
        throw new Error(`Invalid grid index: ${gridIndex} (must be 0-${totalSlots - 1})`);
    }
    if (player.battlefield[gridIndex] !== null) {
        throw new Error(`Grid position ${gridIndex} is already occupied`);
    }
    const card = player.hand[handCardIndex];
    if (!card) {
        throw new Error(`No card at hand index: ${handCardIndex}`);
    }
    const columns = state.params.columns;
    const row = Math.floor(gridIndex / columns);
    const col = gridIndex % columns;
    const hp = card.value;
    const newHand = [...player.hand];
    newHand.splice(handCardIndex, 1);
    const newBattlefield = [...player.battlefield];
    newBattlefield[gridIndex] = {
        card,
        position: { row, col },
        currentHp: hp,
        faceDown: false,
    };
    return setPlayer(state, playerIndex, {
        ...player,
        hand: newHand,
        battlefield: newBattlefield,
    });
}
/**
 * Get the grid index where a deploy card should be placed in a column.
 * Front row first (index = column), then back row (index = column + 4).
 * Returns null if column is full.
 */
export function getDeployTarget(battlefield, column, rows = 2, columns = 4) {
    for (let row = 0; row < rows; row++) {
        const idx = row * columns + column;
        if (battlefield[idx] === null)
            return idx;
    }
    return null;
}
/**
 * Returns a new battlefield array (pure function).
 */
export function advanceBackRow(battlefield, column, rows = 2, columns = 4) {
    const newBf = [...battlefield];
    for (let row = 0; row < rows - 1; row++) {
        const idx = row * columns + column;
        if (newBf[idx] === null) {
            for (let behindRow = row + 1; behindRow < rows; behindRow++) {
                const behindIdx = behindRow * columns + column;
                const behindCard = newBf[behindIdx];
                if (behindCard) {
                    newBf[idx] = { ...behindCard, position: { row, col: column } };
                    newBf[behindIdx] = null;
                    break;
                }
            }
        }
    }
    return newBf;
}
/**
 * Check if both front and back row in a column are occupied.
 */
export function isColumnFull(battlefield, column, rows = 2, columns = 4) {
    for (let row = 0; row < rows; row++) {
        if (battlefield[row * columns + column] === null)
            return false;
    }
    return true;
}
/**
 * Get the grid index where a reinforcement card should be placed.
 * Prioritizes back row, then front row. Returns null if column is full.
 */
export function getReinforcementTarget(battlefield, column, rows = 2, columns = 4) {
    for (let row = rows - 1; row >= 0; row--) {
        const idx = row * columns + column;
        if (battlefield[idx] === null)
            return idx;
    }
    return null;
}
/**
 * Evaluates the current game state to determine if a victory condition has been met.
 */
export function checkVictory(state) {
    // Victory by Pass Limit
    if (state.passState) {
        const rules = state.params.modePassRules;
        for (let i = 0; i < 2; i++) {
            const consecutive = state.passState.consecutivePasses[i] ?? 0;
            const total = state.passState.totalPasses[i] ?? 0;
            if (consecutive >= rules.maxConsecutivePasses || total >= rules.maxTotalPassesPerPlayer) {
                return { winnerIndex: i === 0 ? 1 : 0, victoryType: 'passLimit' };
            }
        }
    }
    for (let i = 0; i < 2; i++) {
        const opponent = state.players[i === 0 ? 1 : 0];
        if (!opponent)
            continue;
        if (opponent.lifepoints <= 0) {
            return { winnerIndex: i, victoryType: 'lpDepletion' };
        }
        const hasBattlefield = opponent.battlefield.some((s) => s !== null);
        const hasHand = opponent.hand.length > 0;
        const hasDrawpile = opponent.drawpile.length > 0;
        if (!hasBattlefield && !hasHand && !hasDrawpile) {
            return { winnerIndex: i, victoryType: 'cardDepletion' };
        }
    }
    // TASK-201: Check for deck exhaustion during initialization
    // If either player has zero cards in their drawpile at match start (and not yet in DeploymentPhase),
    // they cannot proceed.
    if (isStartTurn(state)) {
        for (let i = 0; i < 2; i++) {
            const p = state.players[i];
            if (p?.drawpile.length === 0 && p.hand.length < state.params.initialDraw) {
                return { winnerIndex: i === 0 ? 1 : 0, victoryType: 'cardDepletion' };
            }
        }
    }
    return null;
}
//# sourceMappingURL=state.js.map